import type { Calendar } from './calendar.js';
import { digestLines, digestOf } from './digest.js';
import { CHECKIN_NOTIFICATION_ID, notifId } from './notifId.js';
import { DAY_MS, reminderAlerts } from './reminders.js';
import { isResting, wakeAt } from './repeat.js';
import type { Card } from './types.js';

export type NotifyLang = 'en' | 'fr';

/**
 * The words of the phone's notifications. The app shows the same words elsewhere, and a test
 * holds its dictionaries to these; the server needs them to compose a plan on its own.
 */
export const NOTIFY_TEXT = {
  en: {
    dueToday: 'Today',
    lateToday: 'Overdue',
    lateOne: '1 day late',
    lateOther: '{n} days late',
    checkinTitle: 'How is your energy today?',
    checkinBody: 'Set it and the board fades what you cannot face.',
    digestBack: 'Back today:',
    digestQuiet: 'Gone quiet:',
    digestMore: 'and {n} more',
    columnFirst: 'Dreading',
    columnMiddle: 'Neutral',
    columnLast: 'Eager',
    columnLevel: 'Level {n}',
    importance: ['Must', 'Should', 'Could'],
    horizon: ['Today', 'This week', 'Whenever'],
  },
  fr: {
    dueToday: 'Aujourd’hui',
    lateToday: 'Dépassée',
    lateOne: '1 jour de retard',
    lateOther: '{n} jours de retard',
    checkinTitle: 'Comment va votre énergie aujourd’hui ?',
    checkinBody: 'Réglez-la et le tableau estompe ce que vous ne pouvez pas affronter.',
    digestBack: 'De retour aujourd’hui :',
    digestQuiet: 'Restées silencieuses :',
    digestMore: 'et {n} de plus',
    columnFirst: 'Redouté',
    columnMiddle: 'Neutre',
    columnLast: 'Impatient',
    columnLevel: 'Niveau {n}',
    importance: ['Doit', 'Devrait', 'Pourrait'],
    horizon: ['Aujourd’hui', 'Cette semaine', 'Un jour'],
  },
} as const;

export interface LabelSettings {
  columnCount: number;
  columnLabels: (string | null)[];
  bandLabels: (string | null)[];
  bandPreset: 'importance' | 'horizon';
  /** The admin's names for new accounts; the column names apply to three columns only. */
  defaults: { columnLabels: (string | null)[]; bandLabels: (string | null)[] };
}

/** The user's own name, else the admin's default, else the language's, as the app names them. */
export function resolveLabels(lang: NotifyLang, s: LabelSettings): { column(i: number): string; band(i: number): string } {
  const text = NOTIFY_TEXT[lang];
  const fallbackColumn = (i: number): string => {
    if (i === 0) return text.columnFirst;
    if (i === s.columnCount - 1) return text.columnLast;
    if (s.columnCount === 3) return text.columnMiddle;
    return text.columnLevel.replace('{n}', String(i + 1));
  };
  return {
    column: (i) => s.columnLabels[i] || (s.columnCount === 3 ? s.defaults.columnLabels[i] : null) || fallbackColumn(i),
    band: (i) => s.bandLabels[i] || s.defaults.bandLabels[i] || text[s.bandPreset][i] || '',
  };
}

export interface PlanItem {
  /** The Android notification id: stable per card and purpose, so the phone can diff. */
  id: number;
  kind: 'reminder' | 'checkin' | 'today';
  title: string;
  body: string;
  /** The card's board, named once the user has two or more. */
  board?: string;
  cardId?: string;
  /** When a reminder or the check-in fires; a today entry shows at once. */
  at?: number;
  /** A reminder that rings like an alarm. */
  ring?: boolean;
  /** The check-in's wall-clock time, so the phone re-arms it every day. */
  daily?: { hour: number; minute: number };
}

export interface PlanInput {
  cards: Card[];
  /** The user's boards; names show once there are two. */
  boards: { id: string; name: string }[];
  now: number;
  cal: Calendar;
  lang: NotifyLang;
  labels: LabelSettings;
  checkinTime: string | null;
  /** This phone keeps today's cards in the notification shade. */
  todayShade: boolean;
}

export const TODAY_MAX = 10;

function isOpen(c: Card): boolean {
  return c.deletedAt === null && c.status === 'open';
}

/** Late once the time has passed when there is one, otherwise once the day is over. */
export function isLateIn(dueAt: number, now: number, cal: Calendar): boolean {
  const day = cal.startOfDay(dueAt);
  return dueAt !== day ? now > dueAt : day < cal.startOfDay(now);
}

function dueDaysIn(dueAt: number, now: number, cal: Calendar): number {
  return Math.round((cal.startOfDay(dueAt) - cal.startOfDay(now)) / DAY_MS);
}

/**
 * The cards worth keeping in the shade, across every board: overdue, due today, then every
 * top-band card. Resting recurring cards and the inbox stay out; the list is capped.
 */
export function todayCards(cards: Card[], now: number, cal: Calendar): Card[] {
  const today = cal.startOfDay(now);
  const dueBy = (c: Card) => c.dueAt !== null && cal.startOfDay(c.dueAt) <= today;
  const tier = (c: Card): number => (c.dueAt !== null && isLateIn(c.dueAt, now, cal) ? 0 : dueBy(c) ? 1 : 2);
  return cards
    .filter((c) => isOpen(c) && !c.inbox && !isResting(c, now, cal) && (dueBy(c) || c.band === 0))
    .sort((a, b) => tier(a) - tier(b) || (a.dueAt ?? Number.MAX_SAFE_INTEGER) - (b.dueAt ?? Number.MAX_SAFE_INTEGER) || a.motivation - b.motivation || a.rank - b.rank)
    .slice(0, TODAY_MAX);
}

function todayBody(c: Card, now: number, cal: Calendar, lang: NotifyLang, names: ReturnType<typeof resolveLabels>): string {
  const text = NOTIFY_TEXT[lang];
  const place = `${names.band(c.band)} · ${names.column(c.motivation)}`;
  if (c.dueAt === null) return place;
  if (isLateIn(c.dueAt, now, cal)) {
    const days = dueDaysIn(c.dueAt, now, cal);
    const late = days < 0 ? (days === -1 ? text.lateOne : text.lateOther.replace('{n}', String(-days))) : text.lateToday;
    return `${late} · ${place}`;
  }
  return `${text.dueToday} · ${place}`;
}

/**
 * Everything a phone should hold: each reminder still ahead, the next check-in with its
 * digest, and today's cards when the phone keeps them in the shade. Pure, so the app and the
 * server derive the same plan from the same cards.
 */
export function notificationPlan(input: PlanInput): PlanItem[] {
  const { now, cal, lang } = input;
  const text = NOTIFY_TEXT[lang];
  const open = input.cards.filter(isOpen);
  const named = input.boards.length > 1 ? new Map(input.boards.map((b) => [b.id, b.name])) : null;
  const boardOf = (c: Card) => named?.get(c.boardId);
  const items: PlanItem[] = [];

  for (const c of open) {
    reminderAlerts(c)
      .filter((a) => a.at > now)
      .forEach(({ at, ring }, i) => {
        items.push({ id: notifId(`${c.id}#${i}`), kind: 'reminder', title: c.title, body: c.notes ?? '', board: boardOf(c), cardId: c.id, at, ring });
      });
  }

  const match = input.checkinTime ? /^(\d{1,2}):(\d{2})$/.exec(input.checkinTime) : null;
  if (match) {
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    // The next check-in: today if its time is still ahead, else tomorrow.
    let at = cal.at(now, hour, minute);
    if (at <= now) at = cal.at(cal.addDays(now, 1), hour, minute);
    const wakeDay = (c: Card) => {
      const w = wakeAt(c, cal);
      return w === null ? null : cal.dayKey(w);
    };
    const lines = digestLines(digestOf(open, cal.dayKey(at), now, wakeDay), {
      back: text.digestBack,
      quiet: text.digestQuiet,
      more: (n) => text.digestMore.replace('{n}', String(n)),
    });
    items.push({ id: CHECKIN_NOTIFICATION_ID, kind: 'checkin', title: text.checkinTitle, body: lines.length ? lines.join('\n') : text.checkinBody, at, daily: { hour, minute } });
  }

  if (input.todayShade) {
    const names = resolveLabels(lang, input.labels);
    for (const c of todayCards(open, now, cal)) {
      items.push({ id: notifId(`${c.id}#today`), kind: 'today', title: c.title, body: todayBody(c, now, cal, lang, names), board: boardOf(c), cardId: c.id });
    }
  }
  return items;
}
